import React, { ReactNode, useCallback } from "react";
import styled from "styled-components";
import { Close, Description, Title } from "@radix-ui/react-dialog";
import { Button, Theme } from "@literati/public-ui-shared-react";
import BookShuffleCardDialog, {
  BookShuffleCardDialogClassName,
  BookShuffleCardDialogProps,
} from "components/bookShuffle/BookShuffleCardDialog";
import { BookShuffleDecisionIcon } from "components/bookShuffle/BookShuffleDecisionIcon";
import {
  bookShuffleCardDialogDescriptionFontStyles,
  bookShuffleCardDialogLayoutStyles,
  bookShuffleCardDialogTitleFontStyles,
} from "components/bookShuffle/bookShuffleCardDialogStyles";
import {
  BookShuffleDecisionType,
  SwipeStatus,
} from "components/bookShuffle/bookShuffleTypes";
import analytics from "analytics";
import useBookShuffle from "components/bookShuffle/useBookShuffle";

/** This is the className that will be pre-pended to all of BookShuffleCardDialogDecision's inner component classNames */
export const BOOK_SHUFFLE_CARD_DIALOG_DECISION_CLASS_BASE =
  "book-shuffle-card-dialog-decision";

/** These are all of the internal classNames that are used within the BookShuffleCardDialogDecision component */
export const BookShuffleCardDialogDecisionClassName = {
  BASE: BOOK_SHUFFLE_CARD_DIALOG_DECISION_CLASS_BASE,
} as const;

function getTitle(title: ReactNode, type: BookShuffleDecisionType) {
  if (title) {
    return title;
  }

  switch (type) {
    case SwipeStatus.ACCEPTED:
      return "You swiped right!";
    case SwipeStatus.REJECTED:
      return "You swiped left.";
  }
}

function getDescription(description: ReactNode, type: BookShuffleDecisionType) {
  if (description) {
    return description;
  }

  switch (type) {
    case SwipeStatus.ACCEPTED:
      return "That tells us your reader may be interested in similar books.";
    case SwipeStatus.REJECTED:
      return "That tells us your reader may not be interested in books like this one. ";
  }
}

const StyledBookShuffleCardDialog = styled(BookShuffleCardDialog)`
  & .${BookShuffleCardDialogClassName.CONTENT} {
    ${bookShuffleCardDialogLayoutStyles}
  }
`;

const StyledTitle = styled(Title)`
  margin: 1.5rem 0 0;
  ${bookShuffleCardDialogTitleFontStyles}
`;

const StyledDescription = styled(Description)`
  margin: 0.75rem 0 0;
  ${bookShuffleCardDialogDescriptionFontStyles}
`;

const ButtonContainer = styled.div`
  flex: 2;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
`;

const KEEP_SHUFFLING_LABEL = "Keep Shuffling";

export interface BookShuffleCardDialogDecisionProps
  extends Omit<BookShuffleCardDialogProps, "children"> {
  className?: string;
  description?: ReactNode;
  onButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
  title?: ReactNode;
  decisionType: BookShuffleDecisionType;
}

export const BookShuffleCardDialogDecision = ({
  className = "",
  description = "",
  onButtonClick,
  open,
  title = "",
  decisionType,
  ...dialogProps
}: BookShuffleCardDialogDecisionProps) => {
  const cls = BookShuffleCardDialogDecisionClassName;

  const _getTitle = useCallback(
    () => getTitle(title, decisionType),
    [title, decisionType]
  );
  const _getDescription = useCallback(
    () => getDescription(description, decisionType),
    [description, decisionType]
  );

  return (
    <Theme>
      <StyledBookShuffleCardDialog
        className={`${cls.BASE} ${className}`}
        open={open}
        {...dialogProps}
      >
        <BookShuffleDecisionIcon type={decisionType} />
        <StyledTitle>{_getTitle()}</StyledTitle>
        <StyledDescription>{_getDescription()}</StyledDescription>
        <ButtonContainer>
          <Close asChild>
            <Button
              onClick={(e) => {
                onButtonClick?.(e);
                analytics.trackAmplitudeClickEvent(KEEP_SHUFFLING_LABEL);
              }}
            >
              {KEEP_SHUFFLING_LABEL}
            </Button>
          </Close>
        </ButtonContainer>
      </StyledBookShuffleCardDialog>
    </Theme>
  );
};

export const AcceptedDecisionDialog = () => {
  const { showAcceptedDialog, clearDialogs } = useBookShuffle();

  return (
    <BookShuffleCardDialogDecision
      open={showAcceptedDialog}
      decisionType={SwipeStatus.ACCEPTED}
      onButtonClick={clearDialogs}
    />
  );
};

export const RejectedDecisionDialog = () => {
  const { showRejectedDialog, clearDialogs } = useBookShuffle();

  return (
    <BookShuffleCardDialogDecision
      open={showRejectedDialog}
      decisionType={SwipeStatus.REJECTED}
      onButtonClick={clearDialogs}
    />
  );
};

export default BookShuffleCardDialogDecision;
